/* ============================================================================
 * snd-quiz.js — "Find Your Sound" keyboard quiz engine (section-scoped).
 * ----------------------------------------------------------------------------
 * - Reads the keyboard lineup from a <script type="application/json"> the
 *   section renders from its blocks, so it's all customizer-editable.
 * - No global functions / IDs: each section instance wires itself up via
 *   data-attributes + event delegation, so it's customizer- and multi-instance
 *   safe.
 * - prefers-reduced-motion is honored by the CSS; the logic is unchanged.
 * ========================================================================== */
(function () {
  if (window.__sndQuizInit) return;
  window.__sndQuizInit = true;

  var SCREENS = ['intro', 'q1', 'q2', 'q3', 'q4', 'q5', 'results'];
  var TOTAL = 5;
  var BUDGET_ORDER = ['u70', '70-100', '100-150', '150plus'];

  function scoreKeyboard(kb, a) {
    var s = 0, t = kb.tags;
    // Priority (q1)
    if (a.q1 === 'performance' && t.priority === 'performance') s += 30;
    if (a.q1 === 'look' && t.priority === 'look') s += 25;
    if (a.q1 === 'sound' && (t.priority === 'sound' || t.sound === 'loud')) s += 25;
    if (a.q1 === 'price' && t.priority === 'value') s += 20;
    // Scene (q2)
    if (a.q2 === 'gaming' && t.scene.indexOf('gaming') > -1) s += 30;
    else if (a.q2 === 'office' && t.scene.indexOf('office') > -1) s += 25;
    else if (a.q2 === 'travel' && t.scene.indexOf('travel') > -1) s += 30;
    else if (a.q2 === 'home' && t.scene.indexOf('home') > -1) s += 20;
    // Sound (q3)
    if (a.q3 === t.sound) s += 25;
    else if (a.q3 === 'medium' && t.sound !== 'loud') s += 10;
    if ((a.q2 === 'office' || a.q2 === 'travel') && t.sound === 'loud') s -= 20;
    // Size (q4)
    if (a.q4 === 'notsure') s += 5;
    else if (a.q4 === t.size) s += 20;
    // Budget (q5)
    var max = BUDGET_ORDER.indexOf(a.q5), kbB = BUDGET_ORDER.indexOf(t.budget);
    if (kbB <= max) s += 15;
    if (kbB === max) s += 10;
    if (kbB > max) s -= 30;
    return s;
  }

  function whyReason(kb, a) {
    var t = kb.tags;
    if (t.scene.indexOf('gaming') > -1 && a.q2 === 'gaming') return 'Hall Effect for zero-deadzone competitive response.';
    if (t.sound === 'quiet' && a.q3 === 'quiet') return 'Silent switches for minimal noise output.';
    if (t.priority === 'look' && a.q1 === 'look') return 'Premium design that looks as good as it types.';
    if (t.priority === 'performance') return 'Hall Effect switches for maximum responsiveness.';
    if (t.sound === 'loud' && a.q3 === 'loud') return 'Thocky gasket-mount sound profile you wanted.';
    if (t.scene.indexOf('travel') > -1 && a.q2 === 'travel') return 'Low-profile design built for bags and portability.';
    if (t.priority === 'value') return 'Maximum value at this price — all the key features.';
    return 'Matches your sound, size, and scene preferences.';
  }

  function getRecommendations(kbs, a) {
    return kbs.map(function (kb) { return { kb: kb, score: scoreKeyboard(kb, a) }; })
              .sort(function (x, y) { return y.score - x.score; })
              .slice(0, 3);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function initQuiz(root) {
    var dataEl = root.querySelector('[data-snd-quiz-kb]');
    var KBS = [];
    try { KBS = JSON.parse(dataEl.textContent) || []; } catch (e) { KBS = []; }

    var fill = root.querySelector('[data-snd-progress]');
    var stepInd = root.querySelector('[data-snd-step]');
    var footer = root.querySelector('[data-snd-footer]');
    var btnNext = root.querySelector('[data-snd-next]');
    var btnBack = root.querySelector('[data-snd-back]');
    var nextLabelDefault = btnNext ? btnNext.getAttribute('data-label-next') : 'Next';
    var nextLabelLast = btnNext ? btnNext.getAttribute('data-label-last') : 'See My Results';

    var idx = 0;
    var answers = { q1: null, q2: null, q3: null, q4: null, q5: null };

    function getScreen(name) { return root.querySelector('[data-screen="' + name + '"]'); }

    function showScreen(name, dir) {
      var current = root.querySelector('.snd-quiz__screen.is-active');
      if (current) {
        current.classList.remove('is-active');
        current.classList.add('is-exit');
        setTimeout(function () { current.classList.remove('is-exit'); }, 400);
      }
      var next = getScreen(name);
      if (!next) return;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          next.classList.add('is-active');
        });
      });
    }

    function updateNextBtn() {
      if (!btnNext) return;
      btnNext.disabled = !answers['q' + idx];
      if (btnBack) btnBack.style.visibility = idx <= 1 ? 'hidden' : 'visible';
      btnNext.textContent = (idx === TOTAL) ? nextLabelLast : nextLabelDefault;
    }

    function updateProgress() {
      var qNum = idx - 1;
      var pct = Math.min(100, qNum > 0 ? (qNum / TOTAL) * 100 : 0);
      if (fill) fill.style.width = pct + '%';
      if (idx === 0 || idx >= SCREENS.length - 1) {
        if (footer) footer.style.display = 'none';
        if (stepInd) stepInd.textContent = '';
      } else {
        if (footer) footer.style.display = 'flex';
        if (stepInd) stepInd.textContent = idx + ' / ' + TOTAL;
      }
      updateNextBtn();
    }

    function selectOption(el) {
      var q = el.getAttribute('data-q');
      var val = el.getAttribute('data-val');
      root.querySelectorAll('[data-snd-option][data-q="' + q + '"]').forEach(function (o) { o.classList.remove('is-selected'); });
      el.classList.add('is-selected');
      answers['q' + q] = val;
      updateNextBtn();
    }

    function buildResults() {
      var recs = getRecommendations(KBS, answers);
      var titleMap = { sound: 'Keyboards matched to your sound.', look: 'Keyboards built for your desk.', performance: 'Keyboards built to win.', price: 'The best value for how you type.' };
      var descMap = {
        gaming: 'Hall Effect performance matched to how seriously you play.',
        office: 'Quiet, professional, built for all-day desk work.',
        travel: 'Slim, portable, ready for any desk you sit down at.',
        home: 'Three great keyboards matched to your sound and size preferences.'
      };
      var tEl = root.querySelector('[data-snd-results-title]');
      var dEl = root.querySelector('[data-snd-results-desc]');
      if (tEl) tEl.textContent = titleMap[answers.q1] || tEl.getAttribute('data-default') || 'Your Top Picks';
      if (dEl) dEl.textContent = descMap[answers.q2] || dEl.getAttribute('data-default') || 'Three keyboards matched to how and where you type.';

      var c = root.querySelector('[data-snd-result-cards]');
      if (!c) return;
      c.innerHTML = recs.map(function (r, i) {
        var kb = r.kb;
        var badge = i === 0 ? '<div class="snd-quiz__rcard-badge">Best Match</div>' : '';
        var img = kb.img ? '<img src="' + escapeHtml(kb.img) + '" alt="' + escapeHtml(kb.name) + '" loading="lazy" onerror="this.style.display=\'none\'">' : '';
        var price = kb.price ? '<div class="snd-quiz__price">' + escapeHtml(kb.price) + '</div>' : '';
        return '<a href="' + escapeHtml(kb.url || '#') + '" class="snd-quiz__rcard' + (i === 0 ? ' is-top' : '') + '">' +
          badge +
          '<div class="snd-quiz__rcard-img">' + img + '</div>' +
          '<div class="snd-quiz__rcard-body"><h3>' + escapeHtml(kb.name) + '</h3>' + price +
          '<div class="snd-quiz__why">' + escapeHtml(whyReason(kb, answers)) + '</div></div></a>';
      }).join('');
    }

    function start() { idx = 1; showScreen('q1'); updateProgress(); }

    function goNext() {
      if (idx < TOTAL) { idx++; showScreen(SCREENS[idx]); updateProgress(); }
      else { buildResults(); idx = SCREENS.length - 1; showScreen('results'); if (fill) fill.style.width = '100%'; if (footer) footer.style.display = 'none'; if (stepInd) stepInd.textContent = ''; }
    }

    function goBack() { if (idx > 1) { idx--; showScreen(SCREENS[idx], 'back'); updateProgress(); } }

    function restart() {
      Object.keys(answers).forEach(function (k) { answers[k] = null; });
      root.querySelectorAll('[data-snd-option]').forEach(function (o) { o.classList.remove('is-selected'); });
      idx = 0; showScreen('intro', 'back');
      if (fill) fill.style.width = '0%';
      if (footer) footer.style.display = 'none';
      if (stepInd) stepInd.textContent = '';
    }

    root.addEventListener('click', function (e) {
      var opt = e.target.closest('[data-snd-option]');
      if (opt) { selectOption(opt); return; }
      if (e.target.closest('[data-snd-start]')) { start(); return; }
      if (e.target.closest('[data-snd-next]')) { goNext(); return; }
      if (e.target.closest('[data-snd-back]')) { goBack(); return; }
      if (e.target.closest('[data-snd-restart]')) { restart(); return; }
    });

    // keyboard: enter on a focused option selects it
    root.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        var opt = e.target.closest('[data-snd-option]');
        if (opt) { e.preventDefault(); selectOption(opt); }
      }
    });

    updateProgress();
  }

  function boot(scope) {
    var root = (scope && scope.querySelectorAll) ? scope : document;
    root.querySelectorAll('[data-snd-quiz]').forEach(function (el) {
      if (el.__sndQuizReady) return;   // avoid double-binding an already-initialised instance
      el.__sndQuizReady = true;
      initQuiz(el);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); }, { once: true });
  else boot();
  // Theme editor re-renders a section via shopify:section:load (not DOMContentLoaded) —
  // initialise the freshly injected quiz so it stays interactive while editing.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
